import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  Box,
  Typography,
  CircularProgress,
  Paper
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'

interface FileUploaderProps {
  onUpload: (file: File) => void
  isLoading?: boolean
  acceptedTypes?: string[]
}

export default function FileUploader({
  onUpload,
  isLoading = false,
  acceptedTypes = ['.pdf', '.csv', '.xlsx', '.xls']
}: FileUploaderProps) {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      onUpload(acceptedFiles[0])
    }
  }, [onUpload])

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    disabled: isLoading
  })

  return (
    <Paper
      {...getRootProps()}
      sx={{
        p: 4,
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : 'grey.300',
        backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
        cursor: isLoading ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'action.hover'
        }
      }}
    >
      <input {...getInputProps()} />

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight={200}
      >
        {isLoading ? (
          <>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body1" color="textSecondary">
              Uploading...
            </Typography>
          </>
        ) : acceptedFiles.length > 0 ? (
          <>
            <InsertDriveFileIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="body1" fontWeight="medium">
              {acceptedFiles[0].name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {(acceptedFiles[0].size / 1024 / 1024).toFixed(2)} MB
            </Typography>
          </>
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop the file here' : 'Drag & drop a file here'}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              or click to select a file
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Supported formats: {acceptedTypes.join(', ')}
            </Typography>
          </>
        )}
      </Box>
    </Paper>
  )
}
